import React, { useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { motion, useInView, AnimatePresence } from 'framer-motion'
import { useOptimizedScroll } from '../hooks/useOptimizedScroll'
import { useIsMobile } from '../hooks/useMediaQuery'
import { PageMobileFooter } from './layout/PageMobileFooter'
import { bookingPageFooter } from './layout/footerConfigs'
import { SEOHead } from './SEO/SEOHead'
import { getStructuredData } from '../config/structuredData'
import { NativeScheduler } from './NativeScheduler'

export const BookingPage: React.FC = () => {
  const isMobile = useIsMobile()

  const prefersReducedMotion = useRef(
    typeof window !== 'undefined'
      ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
      : false
  )

  React.useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  // Use optimized scroll hook to prevent flickering
  const { scrollProgress } = useOptimizedScroll()

  return (
    <>
      {/* SEO Head */}
      <SEOHead
        customTitle="Book a Meeting - Mernaki | Schedule Consultation"
        customDescription="Schedule a consultation with Mernaki. Book a meeting to discuss your recruitment needs and explore our solutions."
        customMetadata={{
          structuredData: getStructuredData('booking')
        }}
      />

      <motion.div
        className="min-h-screen relative"
        style={{
          backgroundColor: '#F7F5F5',
          borderBottomLeftRadius: '48px',
          borderBottomRightRadius: '48px',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.2)',
          overflow: 'hidden',
          zIndex: 10,
          paddingBottom: '100px',
          transform: `translateY(${scrollProgress * -20}px)`
        }}
        transition={{ duration: 0 }}
      >
        {/* Randomly Moving Gradient Orbs */}
        <motion.div
          className="absolute w-96 h-96 rounded-full opacity-20 blur-3xl pointer-events-none"
          style={{
            background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
            top: '10%',
            left: '5%',
            willChange: prefersReducedMotion.current ? 'auto' : 'transform'
          }}
          animate={prefersReducedMotion.current ? {} : {
            x: [0, 85, -65, 0],
            y: [0, -75, 85, 0],
            scale: [1, 1.25, 0.9, 1]
          }}
          transition={{
            duration: 28,
            repeat: Infinity,
            ease: 'easeInOut'
          }}
        />
        <motion.div
          className="absolute w-80 h-80 rounded-full opacity-20 blur-3xl pointer-events-none"
          style={{
            background: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
            top: '45%',
            right: '8%',
            willChange: prefersReducedMotion.current ? 'auto' : 'transform'
          }}
          animate={prefersReducedMotion.current ? {} : {
            x: [0, -95, 75, 0],
            y: [0, 90, -70, 0],
            scale: [1, 0.85, 1.3, 1]
          }}
          transition={{
            duration: 32,
            repeat: Infinity,
            ease: 'easeInOut'
          }}
        />
        <motion.div
          className="absolute w-72 h-72 rounded-full opacity-15 blur-3xl pointer-events-none"
          style={{
            background: 'linear-gradient(135deg, #d299c2 0%, #fef9d7 100%)',
            bottom: '20%',
            left: '15%',
            willChange: prefersReducedMotion.current ? 'auto' : 'transform'
          }}
          animate={prefersReducedMotion.current ? {} : {
            x: [0, 70, -85, 0],
            y: [0, -85, 65, 0],
            scale: [1, 1.1, 0.95, 1]
          }}
          transition={{
            duration: 30,
            repeat: Infinity,
            ease: 'easeInOut'
          }}
        />

        <HeroSection prefersReducedMotion={prefersReducedMotion.current} />
        <ValueSection prefersReducedMotion={prefersReducedMotion.current} />
        <SchedulerSection prefersReducedMotion={prefersReducedMotion.current} />
        <WhatToExpectSection prefersReducedMotion={prefersReducedMotion.current} />
        <FAQSection prefersReducedMotion={prefersReducedMotion.current} />
      </motion.div>

      <FooterSection isMobile={isMobile} />
    </>
  )
}

const HeroSection: React.FC<{ prefersReducedMotion: boolean }> = ({ prefersReducedMotion }) => {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: false, amount: 0.3 })

  return (
    <section ref={ref} className="relative pt-48 pb-24 overflow-hidden" style={{ backgroundColor: 'transparent' }}>
      <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.h1
          className="text-5xl md:text-6xl lg:text-7xl mb-6 leading-tight"
          style={{
            color: '#1A1A1A',
            fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
            fontWeight: 600,
            letterSpacing: '-0.03em'
          }}
          initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, ease: [0.4, 0.0, 0.2, 1] }}
        >
          Book a Consultation
        </motion.h1>
        <motion.p
          className="text-xl md:text-2xl max-w-2xl mx-auto leading-relaxed"
          style={{
            color: '#666666',
            fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
            fontWeight: 500
          }}
          initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.3, ease: [0.4, 0.0, 0.2, 1] }}
        >
          Choose a time that fits your schedule. Our team will guide you through solutions tailored to your business.
        </motion.p>
      </div>
    </section>
  )
}

const ValueSection: React.FC<{ prefersReducedMotion: boolean }> = ({ prefersReducedMotion }) => {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: false, amount: 0.3 })

  return (
    <section ref={ref} className="relative py-16" style={{ backgroundColor: 'transparent' }}>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="p-8 rounded-3xl"
          style={{
            backgroundColor: 'rgba(255, 255, 255, 0.6)',
            border: '1px solid rgba(0, 0, 0, 0.05)'
          }}
          initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, ease: [0.4, 0.0, 0.2, 1] }}
        >
          <h2
            className="text-2xl md:text-3xl mb-6"
            style={{
              color: '#1A1A1A',
              fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
              fontWeight: 600
            }}
          >
            During this session, we'll help you:
          </h2>
          <ul className="space-y-4">
            {[
              'Identify your needs',
              'Recommend the right Mernaki service or product',
              'Give pricing guidance',
              'Show real demos if needed'
            ].map((item, index) => (
              <motion.li
                key={item}
                className="flex items-start gap-3"
                initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, x: -20 }}
                animate={isInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.2 + index * 0.1, ease: [0.4, 0.0, 0.2, 1] }}
              >
                <span style={{ color: '#4A90E2', fontSize: '20px', marginTop: '2px' }}>•</span>
                <span
                  className="text-lg"
                  style={{
                    color: '#666666',
                    fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                    fontWeight: 500
                  }}
                >
                  {item}
                </span>
              </motion.li>
            ))}
          </ul>
          <motion.p
            className="text-base mt-6"
            style={{
              color: '#999999',
              fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
              fontWeight: 400,
              fontStyle: 'italic'
            }}
            initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ duration: 0.8, delay: 0.8, ease: [0.4, 0.0, 0.2, 1] }}
          >
            No pressure — just clear answers.
          </motion.p>
        </motion.div>
      </div>
    </section>
  )
}

const SchedulerSection: React.FC<{ prefersReducedMotion: boolean }> = ({ prefersReducedMotion }) => {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: false, amount: 0.1 })

  return (
    <section ref={ref} className="relative py-16" style={{ backgroundColor: 'transparent' }}>
      {/* Section Header */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mb-12">
        <motion.div
          className="text-center"
          initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, ease: [0.4, 0.0, 0.2, 1] }}
        >
          <h2
            className="text-3xl md:text-4xl mb-4"
            style={{
              color: '#1A1A1A',
              fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
              fontWeight: 600,
              letterSpacing: '-0.02em'
            }}
          >
            Choose Your Time
          </h2>
          <p
            className="text-lg max-w-2xl mx-auto"
            style={{
              color: '#666666',
              fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
              fontWeight: 500
            }}
          >
            Select a time that works best for you. All times are shown in your local timezone.
          </p>
        </motion.div>
      </div>

      {/* Native Scheduler */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="relative"
          initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.2, ease: [0.4, 0.0, 0.2, 1] }}
        >
          <NativeScheduler prefersReducedMotion={prefersReducedMotion} />
        </motion.div>
      </div>

      {/* Trust Indicators */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mt-12">
        <motion.div
          className="flex flex-wrap items-center justify-center gap-8 text-center"
          initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.4, ease: [0.4, 0.0, 0.2, 1] }}
        >
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5" style={{ color: '#10B981' }} fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span
              className="text-sm"
              style={{
                color: '#666666',
                fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                fontWeight: 500
              }}
            >
              Free Consultation
            </span>
          </div>
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5" style={{ color: '#10B981' }} fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span
              className="text-sm"
              style={{
                color: '#666666',
                fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                fontWeight: 500
              }}
            >
              No Commitment Required
            </span>
          </div>
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5" style={{ color: '#10B981' }} fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span
              className="text-sm"
              style={{
                color: '#666666',
                fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                fontWeight: 500
              }}
            >
              Easy to Reschedule
            </span>
          </div>
        </motion.div>
      </div>
    </section>
  )
}

const WhatToExpectSection: React.FC<{ prefersReducedMotion: boolean }> = ({ prefersReducedMotion }) => {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: false, amount: 0.2 })

  const steps = [
    {
      title: 'You receive a confirmation email',
      description: 'Instant confirmation with all meeting details'
    },
    {
      title: 'Add the meeting to Google or iCloud Calendar',
      description: 'One-click calendar integration'
    },
    {
      title: 'A Mernaki specialist joins the call exactly on time',
      description: 'Professional, punctual, prepared'
    },
    {
      title: 'We go through your goals and recommend solutions',
      description: 'Tailored advice for your specific needs'
    },
    {
      title: 'Optional: Live walkthrough of the Mernaki platform',
      description: 'See our solutions in action'
    },
    {
      title: 'Receive a personalized action plan & summary',
      description: 'A clear roadmap for your recruitment success'
    }
  ]

  return (
    <section ref={ref} className="relative py-24" style={{ backgroundColor: 'transparent' }}>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.h2
          className="text-4xl md:text-5xl mb-16 text-center"
          style={{
            color: '#1A1A1A',
            fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
            fontWeight: 600,
            letterSpacing: '-0.02em'
          }}
          initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, ease: [0.4, 0.0, 0.2, 1] }}
        >
          What Happens After You Book
        </motion.h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {steps.map((step, index) => (
            <motion.div
              key={step.title}
              className="p-6 rounded-2xl"
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.6)',
                border: '1px solid rgba(0, 0, 0, 0.05)'
              }}
              initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.1, ease: [0.4, 0.0, 0.2, 1] }}
            >
              <div className="flex items-start gap-4">
                <span
                  className="flex-shrink-0 text-4xl font-bold"
                  style={{
                    color: 'rgba(74, 144, 226, 0.2)',
                    fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                    fontWeight: 700,
                    lineHeight: '1'
                  }}
                >
                  {(index + 1).toString().padStart(2, '0')}
                </span>
                <div className="flex-1">
                  <h3
                    className="text-base mb-2"
                    style={{
                      color: '#1A1A1A',
                      fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                      fontWeight: 600
                    }}
                  >
                    {step.title}
                  </h3>
                  <p
                    className="text-sm"
                    style={{
                      color: '#999999',
                      fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                      fontWeight: 400
                    }}
                  >
                    {step.description}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}

const FAQSection: React.FC<{ prefersReducedMotion: boolean }> = ({ prefersReducedMotion }) => {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: false, amount: 0.2 })
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null)

  const faqs = [
    {
      q: 'Do I need to prepare anything?',
      a: 'Not required — but having your website or needs ready helps us provide more specific recommendations.'
    },
    {
      q: 'Is the call free?',
      a: 'Yes, your introductory consultation is completely free with no obligations.'
    },
    {
      q: 'How long does it last?',
      a: 'Most sessions take between 15–25 minutes, but we can extend if needed to ensure all your questions are answered.'
    },
    {
      q: 'What if I need to reschedule?',
      a: 'You can reschedule or cancel anytime using the link in your confirmation email.'
    },
    {
      q: 'Will I get a recording of the call?',
      a: 'We can provide a summary of recommendations discussed. Recording is available upon request for enterprise clients.'
    }
  ]

  return (
    <section ref={ref} className="relative py-24" style={{ backgroundColor: 'transparent' }}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.h2
          className="text-4xl md:text-5xl mb-16 text-center"
          style={{
            color: '#1A1A1A',
            fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
            fontWeight: 600,
            letterSpacing: '-0.02em'
          }}
          initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, ease: [0.4, 0.0, 0.2, 1] }}
        >
          Frequently Asked Questions
        </motion.h2>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              layout
              className="rounded-2xl overflow-hidden"
              style={{
                backgroundColor: '#FFFFFF',
                border: '1px solid rgba(0, 0, 0, 0.1)'
              }}
              initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.1, ease: [0.4, 0.0, 0.2, 1] }}
            >
              <button
                className="w-full p-6 text-left flex items-center justify-between transition-colors duration-300"
                onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                style={{ backgroundColor: expandedFaq === index ? '#F9FAFB' : 'transparent' }}
              >
                <h3
                  className="text-lg pr-4"
                  style={{
                    color: '#1A1A1A',
                    fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                    fontWeight: 600
                  }}
                >
                  {faq.q}
                </h3>
                <svg
                  className="w-6 h-6 flex-shrink-0 transition-transform duration-300"
                  style={{
                    color: '#666666',
                    transform: expandedFaq === index ? 'rotate(180deg)' : 'rotate(0deg)'
                  }}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              <AnimatePresence>
                {expandedFaq === index && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3, ease: 'easeInOut' }}
                  >
                    <div className="px-6 pb-6">
                      <p
                        className="text-base leading-relaxed"
                        style={{
                          color: '#666666',
                          fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                          fontWeight: 400
                        }}
                      >
                        {faq.a}
                      </p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}

const FooterSection: React.FC<{ isMobile: boolean }> = ({ isMobile }) => {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: false, amount: 0.3 })

  if (isMobile) {
    return <PageMobileFooter {...bookingPageFooter} />
  }

  return (
    <footer ref={ref} className="relative" style={{ backgroundColor: '#0E0E0E', position: 'sticky', bottom: 0, zIndex: 1, paddingTop: '140px', marginTop: '-80px' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="py-16">
          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, ease: [0.4, 0.0, 0.2, 1] }}
          >
            {/* Column 1 - Brand */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img
                  src="/Mernaki_Logo_Pack/transparent/mernaki-128.png"
                  alt="Mernaki Logo"
                  className="object-contain"
                  style={{ height: '42px', width: 'auto', opacity: 0.85, filter: 'brightness(0) invert(1)' }}
                  loading="lazy"
                />
                <h3
                  className="text-lg"
                  style={{
                    color: '#FFFFFF',
                    fontFamily: "'Brush Script MT', 'Lucida Handwriting', 'Apple Chancery', cursive",
                    fontWeight: 400
                  }}
                >
                  Mernaki
                </h3>
              </div>
              <p
                className="text-sm leading-relaxed mb-4"
                style={{
                  color: 'rgba(255, 255, 255, 0.5)',
                  fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                  fontWeight: 400
                }}
              >
                Empowering businesses with modern recruitment tools.
              </p>
              <p
                className="text-xs"
                style={{
                  color: 'rgba(255, 255, 255, 0.3)',
                  fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                  fontWeight: 400
                }}
              >
                © {new Date().getFullYear()} Mernaki. All rights reserved.
              </p>
            </div>

            {/* Column 2 - Company */}
            <div>
              <h3
                className="text-sm mb-4"
                style={{
                  color: 'rgba(255, 255, 255, 0.9)',
                  fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em'
                }}
              >
                Company
              </h3>
              <ul className="space-y-3">
                <li>
                  <Link
                    to="/about"
                    className="text-sm transition-all duration-300"
                    style={{
                      color: 'rgba(255, 255, 255, 0.5)',
                      fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                      fontWeight: 400,
                      textDecoration: 'none'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'}
                    onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'}
                  >
                    About Us
                  </Link>
                </li>
                <li>
                  <Link
                    to="/team"
                    className="text-sm transition-all duration-300"
                    style={{
                      color: 'rgba(255, 255, 255, 0.5)',
                      fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                      fontWeight: 400,
                      textDecoration: 'none'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'}
                    onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'}
                  >
                    Team
                  </Link>
                </li>
                <li>
                  <Link
                    to="/partnerships"
                    className="text-sm transition-all duration-300"
                    style={{
                      color: 'rgba(255, 255, 255, 0.5)',
                      fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                      fontWeight: 400,
                      textDecoration: 'none'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'}
                    onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'}
                  >
                    Partnerships
                  </Link>
                </li>
                <li>
                  <span
                    className="text-sm"
                    style={{
                      color: 'rgba(255, 255, 255, 0.5)',
                      fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                      fontWeight: 400
                    }}
                  >
                    Press
                  </span>
                </li>
              </ul>
            </div>

            {/* Column 3 - Products (Blurred) */}
            <div>
              <h3
                className="text-sm mb-4"
                style={{
                  color: 'rgba(255, 255, 255, 0.9)',
                  fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em'
                }}
              >
                Products
              </h3>
              <ul className="space-y-3">
                <li>
                  <span
                    className="text-sm"
                    style={{
                      color: 'rgba(255, 255, 255, 0.3)',
                      fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                      fontWeight: 400,
                      filter: 'blur(2px)',
                      userSelect: 'none',
                      pointerEvents: 'none'
                    }}
                  >
                    MacX Launcher
                  </span>
                </li>
                <li>
                  <span
                    className="text-sm"
                    style={{
                      color: 'rgba(255, 255, 255, 0.3)',
                      fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                      fontWeight: 400,
                      filter: 'blur(2px)',
                      userSelect: 'none',
                      pointerEvents: 'none'
                    }}
                  >
                    CodeRED IDE
                  </span>
                </li>
                <li>
                  <span
                    className="text-sm"
                    style={{
                      color: 'rgba(255, 255, 255, 0.3)',
                      fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                      fontWeight: 400,
                      filter: 'blur(2px)',
                      userSelect: 'none',
                      pointerEvents: 'none'
                    }}
                  >
                    BeyondReal Tools
                  </span>
                </li>
                <li>
                  <span
                    className="text-sm"
                    style={{
                      color: 'rgba(255, 255, 255, 0.3)',
                      fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                      fontWeight: 400,
                      filter: 'blur(2px)',
                      userSelect: 'none',
                      pointerEvents: 'none'
                    }}
                  >
                    MacX Handheld
                  </span>
                </li>
              </ul>
            </div>

            {/* Column 4 - Support */}
            <div>
              <h3
                className="text-sm mb-4"
                style={{
                  color: 'rgba(255, 255, 255, 0.9)',
                  fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em'
                }}
              >
                Support
              </h3>
              <ul className="space-y-3">
                <li>
                  <Link
                    to="/help"
                    className="text-sm transition-all duration-300"
                    style={{
                      color: 'rgba(255, 255, 255, 0.5)',
                      fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                      fontWeight: 400,
                      textDecoration: 'none'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'}
                    onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'}
                  >
                    Help Center
                  </Link>
                </li>
                <li>
                  <Link
                    to="/faq"
                    className="text-sm transition-all duration-300"
                    style={{
                      color: 'rgba(255, 255, 255, 0.5)',
                      fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                      fontWeight: 400,
                      textDecoration: 'none'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'}
                    onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'}
                  >
                    FAQ
                  </Link>
                </li>
                <li>
                  <Link
                    to="/report-issue"
                    className="text-sm transition-all duration-300"
                    style={{
                      color: 'rgba(255, 255, 255, 0.5)',
                      fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                      fontWeight: 400,
                      textDecoration: 'none'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'}
                    onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'}
                  >
                    Report an Issue
                  </Link>
                </li>
                <li>
                  <Link
                    to="/contact"
                    className="text-sm transition-all duration-300"
                    style={{
                      color: 'rgba(255, 255, 255, 0.5)',
                      fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                      fontWeight: 400,
                      textDecoration: 'none'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'}
                    onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'}
                  >
                    Contact
                  </Link>
                </li>
              </ul>
            </div>

            {/* Column 5 - Legal */}
            <div>
              <h3
                className="text-sm mb-4"
                style={{
                  color: 'rgba(255, 255, 255, 0.9)',
                  fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em'
                }}
              >
                Legal
              </h3>
              <ul className="space-y-3">
                <li>
                  <Link
                    to="/privacy"
                    className="text-sm transition-all duration-300"
                    style={{
                      color: 'rgba(255, 255, 255, 0.5)',
                      fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                      fontWeight: 400,
                      textDecoration: 'none'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'}
                    onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'}
                  >
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link
                    to="/terms"
                    className="text-sm transition-all duration-300"
                    style={{
                      color: 'rgba(255, 255, 255, 0.5)',
                      fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                      fontWeight: 400,
                      textDecoration: 'none'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'}
                    onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'}
                  >
                    Terms of Service
                  </Link>
                </li>
                <li>
                  <Link
                    to="/cookies"
                    className="text-sm transition-all duration-300"
                    style={{
                      color: 'rgba(255, 255, 255, 0.5)',
                      fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                      fontWeight: 400,
                      textDecoration: 'none'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'}
                    onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'}
                  >
                    Cookie Policy
                  </Link>
                </li>
                <li>
                  <Link
                    to="/impressum"
                    className="text-sm transition-all duration-300"
                    style={{
                      color: 'rgba(255, 255, 255, 0.5)',
                      fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                      fontWeight: 400,
                      textDecoration: 'none'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'}
                    onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'}
                  >
                    Impressum
                  </Link>
                </li>
              </ul>
            </div>
          </motion.div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t py-6" style={{ borderColor: 'rgba(255, 255, 255, 0.1)' }}>
          <motion.div
            className="text-center"
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ duration: 0.8, delay: 0.4, ease: [0.4, 0.0, 0.2, 1] }}
          >
            <p className="text-xs" style={{ color: 'rgba(255, 255, 255, 0.4)', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400 }}>
              Built in Germany | Secure & GDPR Compliant
            </p>
          </motion.div>
        </div>
      </div>
    </footer>
  )
}
